"use client";

import Image from "next/image";
import { useEffect, useId, useRef, useState, type CSSProperties } from "react";
import { ArrowRight, Check, Layers3, Sparkles, X } from "lucide-react";
import {BiteCreditIcon,BiteCreditText} from './bite-credit';
import { BiteButton, BiteLinkButton, BiteReportProgress, type BiteProgressSection } from "./bite-ui";
import { cn } from "@/lib/utils";
import {formatBitePlanPrice} from '@/lib/bite-plan-price';
import { stripName } from "@/lib/strip-name";
import { BiteCinemaFrame } from "./bite-cinema-frame";
import {useBiteScreen} from './use-bite-screen';
import {useBitePresentation} from './use-bite-presentation';
import type { BiteGenerationKind } from "./bite-generation-video";
import { BiteCompanyLogo } from "./bite-company-logo";
import { getScoreLabel } from "@/components/report/score-ring";
import {BiteReportScoreScale,biteAssessment} from './bite-report-score-scale';
import { BiteBstMetrics, type BiteBstAnalysis } from "./bite-bst-metrics";
import { BiteCardArt, BiteSavedCardArt, useBiteCardArt, type BiteCardArtState } from "./bite-card-art";
import {useBiteMemberPrice} from '@/hooks/use-bite-member-price';
import pricing from '../../../../config/bite-plans.json';
import rarities from '../../../../config/bite-card-rarities.json';
import {BiteCardOdds} from './bite-card-odds';
import {BiteCompletionOfferBanner,offerAmount,useOfferRemaining,type BiteCompletionOffer} from './bite-completion-offer';
import {BITE_MONTHLY_TERMS, type BiteReportAccess} from '@/lib/bite-access';
import {bitePlanOfferings} from '@/lib/bite-plan-offerings';
export type { BiteBstAnalysis } from "./bite-bst-metrics";

export type BitePackId = "normal" | "rare" | "epic" | "unique" | "legendary";

export interface BiteCardMetric {
  label: string;
  score: number;
}

export interface BiteOwnedCardData {
  jobId: string;
  ticker: string;
  packId: BitePackId;
  companyName: string;
  score: number | null;
  scores?: BiteCardMetric[];
  bstAnalysis?: BiteBstAnalysis | null;
  art?: BiteCardArtState;
  reportCreatedAt: string | null;
  openedAt: string;
  reportAccess?: BiteReportAccess;
  reading?: import('./bite-report-tools').BiteReadingPosition | null;
}
export type BiteReportPreview = Pick<BiteOwnedCardData, 'ticker' | 'companyName' | 'score' | 'scores' | 'reportCreatedAt'> & {locked?:boolean;chapters?:{id:string;title:string}[]};

const PACKS = rarities.map(pack=>({...pack,id:pack.id as BitePackId,rarity:pack.id as BitePackId,probability:pack.weight}));

const packFor = (packId: BitePackId) => PACKS.find((pack) => pack.id === packId) || PACKS[0];
const dropRateCopy = `표지 확률은 ${PACKS.map((pack) => `${pack.rarityKo} ${pack.probability}%`).join(", ")}입니다.`;

export function BiteBullsCardFace({
  ticker,
  companyName,
  packId,
  analysis,
  jobId,
  art,
  readOnly = false,
}: {
  ticker: string;
  companyName?: string;
  packId: BitePackId;
  analysis?: BiteBstAnalysis | null;
  jobId?: string;
  art?: BiteCardArtState;
  readOnly?: boolean;
}) {
  const pack = packFor(packId);
  const displayName = analysis?.name || stripName(companyName) || ticker;
  const face = (
    <div className={cn("bite-bulls-card", `r-${pack.rarity}`)}>
      <div className="bite-bulls-card__foil" />
      <div className="bite-bulls-card__stripes" />
      {readOnly ? <BiteSavedCardArt art={art} companyName={displayName}/> : <BiteCardArt jobId={jobId} initial={art} companyName={displayName} />}
      <div className="bite-bulls-card__sheen" />
      <div className="bite-bulls-card__inner" />
      <div className="bite-bulls-card__rarity">{pack.rarityLabel}</div>
      {analysis && <div className="bite-bulls-card__head" aria-label={`BST 기업 평가 ${analysis.score}점, 10점 만점, ${analysis.grade}등급`}>
        <div className="bite-bulls-card__score">{analysis.score.toFixed(1)}</div>
        <div className="bite-bulls-card__grade">{analysis.grade}</div>
      </div>}
      <div className="bite-bulls-card__lower">
        <div className="bite-bulls-card__title">
          <div className="bite-bulls-card__name">{displayName}</div>
          {ticker.toUpperCase() !== displayName.toUpperCase() && <div className="bite-bulls-card__ticker">{ticker}</div>}
        </div>
        <div className="bite-bulls-card__rule" />
        <BiteBstMetrics analysis={analysis} />
        <div className="bite-bulls-card__brand"><Image src="/bite/bst-bull-logo.png" alt="" width={22} height={22} /><span>Bullstory</span></div>
      </div>
    </div>
  );
  return pack.rarity === "legendary" ? <div className="bite-bulls-card-prism">{face}</div> : face;
}

export function BiteMysteryReport({
  ticker,
  companyName,
  disabled = false,
  onReveal,
  variant = "default",
}: {
  ticker: string;
  companyName?: string;
  disabled?: boolean;
  onReveal: () => void;
  variant?: "default" | "cover";
}) {
  return (
    <button
      type="button"
      className={cn("bite-mystery-report", variant === "cover" && "bite-mystery-report--cover")}
      onClick={onReveal}
      disabled={disabled}
      aria-label={`${companyName || ticker} 보고서 표지 뽑기`}
    >
      {variant === "default" && <><span className="bite-mystery-report__spark bite-mystery-report__spark--one" aria-hidden="true" />
      <span className="bite-mystery-report__spark bite-mystery-report__spark--two" aria-hidden="true" />
      <span className="bite-mystery-report__spark bite-mystery-report__spark--three" aria-hidden="true" /></>}
      <span className="bite-mystery-report__cover" aria-hidden="true">
        <span className="bite-mystery-report__brand">BITE</span>
        <strong>?</strong>
        <span className="bite-mystery-report__ticker">{ticker}</span>
        <small>기업분석 보고서</small>
      </span>
      {variant === "default" && <span className="bite-mystery-report__action">
        <Sparkles aria-hidden="true" />
        <span><strong>표지를 뽑고 보고서 완성하기</strong><small>{dropRateCopy}</small></span>
        <ArrowRight aria-hidden="true" />
      </span>}
    </button>
  );
}

export function BitePurchaseChoices({
  expired = false,
  ticker,
  busy,
  onPurchase,
  offer,
  focusOnMount = false,
  preferredPlan,
}: {
  ticker: string;
  busy: boolean;
  onPurchase: (plan: "card" | "monthly", expectedAmount: number) => void;
  expired?: boolean;
  offer?: BiteCompletionOffer | null;
  focusOnMount?: boolean;
  preferredPlan?: 'card' | 'monthly' | null;
}) {
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (!focusOnMount) return;
    heading.current?.focus({ preventScroll: true });
    heading.current?.scrollIntoView({ block: 'start' });
  }, [focusOnMount]);
  const offerSeconds = useOfferRemaining(offer);
  const membership = useBiteMemberPrice();
  const memberPrice = membership.value?.premiumMember ? membership.value.memberPrice : null;
  const amounts={card:offerAmount(offer,'card'),monthly:memberPrice?.amount ?? offerAmount(offer,'monthly')};
  return (
    <section className="bite-purchase" aria-label="보고서 결제 방식">
      <header>
        <p>{ticker} 기업분석 보고서</p>
        <h2 ref={heading} tabIndex={-1}>{expired ? '보고서 이용 기간이 끝났습니다.' : '내 종목의 대응, 지금 확인하세요.'}</h2>
        {expired && <p>소장한 캐릭터 카드는 그대로 남아 있습니다. 보고서를 다시 읽으려면 아래에서 이용 방법을 선택해주세요.</p>}
      </header>
      <BiteCompletionOfferBanner offer={offer} showPrices={false}/>
      <div className="bite-purchase__options">
        {[...bitePlanOfferings].sort((a,b) => Number(b.id === preferredPlan) - Number(a.id === preferredPlan)).map(plan => <article className="bite-purchase__plan" data-plan={plan.id} key={plan.id} aria-label={plan.name}>
          <div className="bite-purchase__plan-heading">
            {plan.id === 'card' ? <BiteCreditIcon/> : <Layers3 aria-hidden="true" />}
            <h3>{plan.name}</h3>
          </div>
          <p className="bite-purchase__description">{plan.description}</p>
          {plan.id === 'monthly' && (memberPrice ? <span className="bite-purchase__discount">프리미엄 구독자는 약 {memberPrice.savingsPercent}% 절약</span> : offerSeconds > 0 && <span className="bite-purchase__discount">50% 할인</span>)}
          <p className="bite-purchase__price">{plan.id === 'monthly' && (memberPrice ? <del>{formatBitePlanPrice(memberPrice.regularAmount)}원</del> : offerSeconds > 0 && offer && <del>{formatBitePlanPrice(offer.plans[plan.id].regularAmount)}원</del>)}<b>{plan.id === 'monthly' ? '월 ' : ''}{formatBitePlanPrice(amounts[plan.id])}원</b><small>{plan.period}</small></p>
          <ul className="bite-purchase__benefits">{plan.benefits.map(benefit => <li key={benefit}><Check aria-hidden="true" /><span><BiteCreditText>{benefit}</BiteCreditText></span></li>)}</ul>
          <BiteButton size="lg" variant={plan.id === 'monthly' ? 'accent' : 'secondary'} disabled={busy || (plan.id === 'monthly' && (!membership.ready || membership.error))} onClick={() => onPurchase(plan.id, amounts[plan.id])}>
            <BiteCreditText>{busy ? '결제를 연결하고 있습니다' : plan.id === 'card' ? '30크레딧 충전하기' : '월간 패스 시작하기'}</BiteCreditText><ArrowRight aria-hidden="true" />
          </BiteButton>
        </article>)}
      </div>
      {membership.error && <BiteButton variant="ghost" onClick={membership.retry}>프리미엄 혜택 다시 확인하기</BiteButton>}
      <p className="bite-purchase__account">구매 내역은 로그인한 계정에 저장됩니다.</p>
      <p className="bite-caption">{BITE_MONTHLY_TERMS}</p>
    </section>
  );
}

export function BitePriceSummary(){return <p className="bite-price-summary"><BiteCreditText>{`30크레딧은 ${formatBitePlanPrice(pricing.plans.card.amount)}원이며, 월간 패스는 매월 300크레딧을 제공합니다.`}</BiteCreditText></p>;}

export function BiteOwnedCard({
  card,
  revealed = false,
  onOpen,
}: {
  card: BiteOwnedCardData;
  revealed?: boolean;
  onOpen: () => void;
}) {
  return (
    <article className={cn("bite-owned-card", `bite-owned-card--${card.packId}`, revealed && "is-revealed")}>
      <BiteBullsCardFace ticker={card.ticker} companyName={card.companyName} packId={card.packId} analysis={card.bstAnalysis} jobId={card.jobId} art={card.art} />
      <BiteButton type="button" onClick={onOpen}>
        보고서 읽기 <ArrowRight aria-hidden="true" />
      </BiteButton>
    </article>
  );
}

export function BiteReportDocument({ card, assessment = false, logoSrc, details, headerScore, showBrand = true }: { card: BiteReportPreview | BiteOwnedCardData; assessment?: boolean; logoSrc?:string; details?:React.ReactNode; headerScore?:number|null; showBrand?:boolean }) {
  const score = card.score != null && Number.isFinite(card.score) ? Math.max(0, Math.min(100, Math.round(card.score))) : null;
  const created = card.reportCreatedAt ? new Date(card.reportCreatedAt) : null;
  const date = created && Number.isFinite(created.getTime()) ? new Intl.DateTimeFormat("ko-KR", { year: "numeric", month: "long", day: "numeric", timeZone: "Asia/Seoul" }).format(created) : null;
  return <article className="bite-report-document" aria-label={`${card.ticker} 기업분석 보고서 표지`}>
    <header>{showBrand&&<span>Bullstory Research</span>}{headerScore!=null?<span className="bite-home-report__average" aria-label={`장단기 평균 ${headerScore}점`} data-tier={biteAssessment(headerScore).tier}><strong>{headerScore}</strong><small> / 100</small></span>:<span>{card.ticker}</span>}</header>
    <div className="bite-report-document__identity">
      <BiteCompanyLogo ticker={card.ticker} companyName={card.companyName} logoSrc={logoSrc}/>
      <div className="bite-report-document__title"><p>{headerScore!=null?card.ticker:'기업분석 보고서'}</p><h2>{stripName(card.companyName) || card.ticker}</h2></div>
    </div>
    {details || (score != null && <section className="bite-report-document__evaluation" aria-label="기업 종합 평가">
      <div className="bite-report-document__summary">
        <div className="bite-report-document__score"><span>종합 평가</span><p>{score}<small> / 100</small></p></div>
        <div className="bite-report-document__grade"><span>{assessment?'분석 의견':'평가 등급'}</span><strong data-tier={assessment?biteAssessment(score).tier:undefined}>{assessment?biteAssessment(score).label:getScoreLabel(score)}</strong></div>
      </div>
      <BiteReportScoreScale score={score} variant={assessment?'assessment':'document'}/>
    </section>)}
    {'locked' in card && card.locked && <section className="bite-report-document__evaluation bite-report-document__contents">
      <h3>이 보고서에서 확인할 내용</h3>
      {'chapters' in card && <ol>{card.chapters?.map(chapter=><li key={chapter.id}>{chapter.title}</li>)}</ol>}
      <p>전체 보고서와 캐릭터 카드가 함께 포함됩니다.</p>
    </section>}
    {!details&&<dl>{(card.scores || []).filter(metric => Number.isFinite(metric.score)).slice(0, 3).map(metric => <div key={metric.label}><dt>{metric.label}</dt><dd>{Math.round(metric.score)}<small> / 100</small></dd></div>)}</dl>}
    <footer><span>{date || card.ticker}</span><span>Bite</span></footer>
  </article>;
}

export function BiteReportStage({ ticker, companyName, card, preview, revealed, sections, opening, onReveal, onOpen, purchase, creditCost, immersive = false, generationKind = "existing", generationRequestId, existingReport = false }: {
  ticker: string;
  companyName?: string;
  card: BiteOwnedCardData | null;
  preview?: BiteReportPreview | null;
  purchase?: React.ReactNode;
  revealed: boolean;
  sections: BiteProgressSection[];
  opening: boolean;
  onReveal: () => void;
  onOpen: () => void;
  creditCost?:number|null;
  immersive?: boolean;
  generationKind?: BiteGenerationKind;
  generationRequestId?: string;
  existingReport?: boolean;
}) {
  const preparedArt = useBiteCardArt(card?.jobId, card?.art);
  const document = card || preview;
  const presentation=useBitePresentation(!!document,immersive&&!existingReport,revealed);
  const {fullscreen,changeFullscreen,finish}=presentation;
  const ready=existingReport?!!document:presentation.ready;
  const observedRoot=useRef<HTMLDivElement>(null);
  useBiteScreen(observedRoot,ready&&!purchase,'completion_view',`${ticker}:${document?.reportCreatedAt}:${generationRequestId||''}`,{ticker,generationRequestId});
  const state = !document ? "working" : !ready ? "ending" : revealed ? "revealed" : "ready";
  if (purchase) return <section className="bite-report-stage__paywall bite-scope bite-light" aria-label="보고서 이용 요금제">{purchase}</section>;
  if (ready && document) return <div ref={observedRoot} className="bite-completion" data-stage={state} aria-label={`${companyName || ticker} 보고서 준비 완료`}>
    <div className="bite-completion__report">
    <h1 className="bite-completion__title">{stripName(document.companyName) || ticker} 기업분석 보고서</h1>
    <div className="bite-completion__cover">
      {revealed && card ? <BiteBullsCardFace ticker={card.ticker} companyName={card.companyName} packId={card.packId} analysis={card.bstAnalysis} jobId={card.jobId} art={preparedArt.state} /> : <BiteReportDocument card={document} />}
    </div>
    </div>
    <div className="bite-completion__actions">
      <BiteButton onClick={onOpen} disabled={opening} data-track="bite.report_open"><BiteCreditText>{opening ? '열고 있습니다' : creditCost ? `열람하기 ${creditCost}크레딧` : '열람하기'}</BiteCreditText><ArrowRight aria-hidden="true" /></BiteButton>
      {revealed ? <BiteLinkButton href="/bite/collection" variant="secondary">내 보관함</BiteLinkButton> : <BiteButton onClick={onReveal} disabled={opening} variant="secondary" data-track="bite.character_collect"><BiteCreditText>{creditCost ? `카드로 소장하기 ${creditCost}크레딧` : '카드로 소장하기'}</BiteCreditText></BiteButton>}
    </div>
  </div>;
  return <BiteCinemaFrame initialFullscreen={immersive&&!existingReport} working={!ready} onFullscreenChange={changeFullscreen} title={`${companyName || ticker} 보고서 생성`}>
  <div ref={observedRoot} className="bite-report-stage" data-stage={state}>
    <BiteReportProgress ticker={ticker} companyName={companyName} sections={sections} complete={ready} videoActive={fullscreen}
      videoPhase={state === "ending" ? "ending" : "generation"} generationKind={generationKind} onVideoFinished={finish}
      footer={ready ? <div className="bite-report-stage__action">
        <div className="bite-report-stage__choices">
          <BiteButton onClick={onOpen} disabled={opening} data-track="bite.report_open">
            <BiteCreditText>{opening ? "보고서를 펼치고 있습니다" : existingReport ? creditCost ? `열람하기 ${creditCost}크레딧` : '열람하기' : creditCost ? `보고서 확인하기 ${creditCost}크레딧` : "보고서 확인하기"}</BiteCreditText>
          </BiteButton>
          {revealed ? <BiteLinkButton href="/bite/collection" variant="secondary">내 보관함 확인하기</BiteLinkButton>
            : <BiteButton onClick={onReveal} disabled={opening} variant="secondary" data-track="bite.character_collect"><Layers3 aria-hidden="true" /><BiteCreditText>{creditCost?`카드로 소장하기 ${creditCost}크레딧`:'카드로 소장하기'}</BiteCreditText></BiteButton>}
        </div>
      </div> : undefined}>
      {document && <div className="bite-report-stage__completion">
        <div className="bite-report-stage__cover-slot"><div className="bite-report-stage__result" data-cover={revealed ? "character" : "document"}>
          {!revealed && <div className="bite-report-stage__card-back" aria-hidden="true"><span>BITE</span><span>COLLECTION</span></div>}
          {revealed && card ? <BiteBullsCardFace ticker={card.ticker} companyName={card.companyName} packId={card.packId} analysis={card.bstAnalysis} jobId={card.jobId} art={preparedArt.state} />
            : <BiteReportDocument card={document} />}
        </div>
        </div><div className="bite-report-stage__message" role="status">
          <span className="bite-report-stage__complete-label"><Check aria-hidden="true" />{existingReport?'이미 생성된 보고서가 있습니다.':'생성 완료'}</span>
          {revealed && card ? <><h2 className="bite-report-stage__obtained">{card.bstAnalysis?.name || stripName(card.companyName) || card.ticker}의<br />{packFor(card.packId).rarityKo} 카드를 획득했습니다.</h2><p>내 보관함에 저장되었습니다.</p></> : <p className="bite-report-stage__collection-note"><Layers3 aria-hidden="true" />{card ? '소장 카드 1장이 함께 저장됐습니다.' : '보고서를 확인하거나 캐릭터 카드로 소장할 수 있습니다.'}</p>}
          <BiteCardOdds packId={revealed&&card?card.packId:undefined}/>
        </div>
      </div>}
    </BiteReportProgress>
  </div>
  </BiteCinemaFrame>;
}

export function BiteReportAssistant({ message, variant = "inline" }: { message: string; variant?: "inline" | "dock" }) {
  const [expanded, setExpanded] = useState(false);
  const id = useId();
  return (
    <aside className={cn("bite-pack-assistant", `bite-pack-assistant--${variant}`)} data-expanded={expanded} aria-label="Bite 보고서 안내">
      {variant === "dock" && <BiteButton className="bite-pack-assistant__toggle" variant="secondary" size="sm" aria-expanded={expanded} aria-controls={id} onClick={() => setExpanded(!expanded)}>
        {expanded ? <X aria-hidden="true" /> : <Sparkles aria-hidden="true" />} {expanded ? "안내 닫기" : "보고서 안내"}
      </BiteButton>}
      <div className="bite-pack-assistant__visual" id={id}>
      <div className="bite-pack-assistant__bubble"><BiteCreditText>{message}</BiteCreditText></div>
      <Image
        src="/cards/bite-card-assistant.png"
        alt="기업분석 보고서를 안내하는 Bite 어시스턴트"
        width={1024}
        height={1536}
        priority
      />
      </div>
    </aside>
  );
}

export { PACKS as BITE_REPORT_COVERS };
